import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';

const rosterClient = createHttpClient(serviceUrls.roster);

/**
 * Class item returned by GET /v1/roster/teacher/me/classes.
 * Returns only classes where the current user is primary or secondary teacher.
 */
export interface TeacherClass {
  id: string;
  tenant_id?: string;
  name: string;
  grade?: string | number | null;
  academic_year_key?: string | null;
  status?: string | null;
  primary_teacher_platform_user_id?: string | null;
  secondary_teacher_platform_user_ids?: string[] | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
}

export const getTeacherMyClasses = async (): Promise<TeacherClass[]> => {
  const response = await rosterClient.get<TeacherClass[]>('/v1/roster/teacher/me/classes');
  return Array.isArray(response.data) ? response.data : [];
};

export interface TeacherSectionItem {
  section: {
    id: string;
    name: string;
    academic_year_key?: string;
    status?: string;
  };
  class: {
    id: string;
    name: string;
    grade?: string;
    academic_year_key?: string;
  };
  students: Array<{
    id: string;
    display_name?: string;
    admission_no?: string;
    status?: string;
  }>;
}

export interface TeacherSectionsResponse {
  items: TeacherSectionItem[];
  total: number;
  skip: number;
  limit: number;
}

export const getTeacherMySections = async (params?: {
  skip?: number;
  limit?: number;
  academic_year_key?: string;
  grade?: number;
}) => {
  const response = await rosterClient.get<TeacherSectionsResponse>('/v1/roster/teacher/me/sections', {
    params,
  });
  return response.data.items;
};

export interface TeacherStudentEntry {
  student: {
    id: string;
    /** Required for learning-service targeting; use this when posting students targets */
    platform_user_id?: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
    admission_no?: string;
    status?: string;
  };
  class: {
    id: string;
    name: string;
    grade?: string;
    academic_year_key?: string;
  };
  section: {
    id: string;
    name?: string;
  } | null;
  enrollment: {
    id: string;
    status?: string;
  };
}

export const getTeacherMyStudents = async (params?: {
  section_id?: string;
  academic_year_key?: string;
  grade?: number;
  q?: string;
  status?: string;
}): Promise<TeacherStudentEntry[]> => {
  const cleanParams: Record<string, string | number> = {};
  if (params?.section_id) cleanParams.section_id = params.section_id;
  if (params?.academic_year_key) cleanParams.academic_year_key = params.academic_year_key;
  if (params?.grade != null) cleanParams.grade = params.grade;
  if (params?.q?.trim()) cleanParams.q = params.q.trim();
  if (params?.status?.trim()) cleanParams.status = params.status.trim();

  const response = await rosterClient.get<TeacherStudentEntry[]>('/v1/roster/teacher/me/students', {
    params: Object.keys(cleanParams).length ? cleanParams : undefined,
  });
  return response.data;
};

export interface TeacherProfilePayload {
  id: string;
  tenant_id: string;
  platform_user_id?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  employment?: {
    employee_code?: string;
    joining_date?: string;
  };
  notes?: string;
}

export const getTeacherProfile = async (): Promise<TeacherProfilePayload> => {
  const response = await rosterClient.get<TeacherProfilePayload>('/v1/roster/teachers/me');
  return response.data;
};

/**
 * PATCH /v1/roster/teachers/me — allowed self-edit fields only.
 * Do not send email, employment, status, etc. (422 extra_forbidden).
 */
export interface TeacherSelfProfilePatchPayload {
  display_name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  notes?: string;
}

export const updateTeacherProfile = async (payload: TeacherSelfProfilePatchPayload) => {
  const response = await rosterClient.patch<TeacherProfilePayload>('/v1/roster/teachers/me', payload);
  return response.data;
};
